// ============================================================
// ORDER REJECTIONS PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { XCircle } from 'lucide-react';
import { Table, Badge, Button, Modal, StatCard } from '../components/ui';
import { getRejections } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatDateTime, formatCurrency } from '../utils/helpers';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const OrderRejections = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(null);

  useEffect(() => {
    adminApi.getRejections(getRejections)
      .then(res => {
        const items = Array.isArray(res) ? res : (res?.items || []);
        setData(items.map(o => ({
          ...o,
          orderId:        o.orderId || o.order_id || o.id,
          customerName:   o.customerName || o.customer_name || o.customer || '—',
          restaurantName: o.restaurantName || o.restaurant_name || o.restaurant || '—',
          amount:         o.amount || o.total_amount || 0,
          rejectedBy:     o.rejectedBy || o.rejected_by || 'restaurant',
          reason:         o.reason || o.rejection_reason || '—',
          rejectedAt:     o.rejectedAt || o.rejected_at || o.createdAt,
        })));
        setLoading(false);
      })
      .catch(e => { setError(e.message || 'Failed to load data'); setLoading(false); });
  }, []);

  // Aggregate by reason
  const reasonMap = {};
  data.forEach(r => { reasonMap[r.reason] = (reasonMap[r.reason] || 0) + 1; });
  const chartData = Object.entries(reasonMap).map(([reason, count]) => ({ reason: reason.slice(0, 20), count })).sort((a, b) => b.count - a.count);

  const bySource = { restaurant: 0, customer: 0, system: 0 };
  data.forEach(r => { if (bySource[r.rejectedBy] !== undefined) bySource[r.rejectedBy]++; });

  const columns = [
    { key: 'orderId', label: 'Order ID', render: v => <code>#{v}</code> },
    { key: 'customerName', label: 'Customer', render: v => <b>{v}</b> },
    { key: 'restaurantName', label: 'Restaurant' },
    { key: 'amount', label: 'Amount', render: v => formatCurrency(v) },
    { key: 'reason', label: 'Rejection Reason' },
    { key: 'rejectedBy', label: 'Rejected By', render: v => <Badge status={v === 'restaurant' ? 'warning' : v === 'customer' ? 'info' : 'danger'} text={v} /> },
    { key: 'rejectedAt', label: 'Date & Time', render: v => formatDateTime(v) },
    { key: 'id', label: 'Action', render: (_, row) => <Button size="sm" variant="secondary" onClick={() => setView(row)}>Details</Button> },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Order Rejections</h1>
          <p className="admin-page-subtitle">Track and analyze rejected orders</p>
        </div>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Rejections" value={data.length} icon={<XCircle size={18} />} color="#EF4444" />
        <StatCard label="By Restaurant" value={bySource.restaurant} icon={<XCircle size={18} />} color="#F59E0B" />
        <StatCard label="By Customer" value={bySource.customer} icon={<XCircle size={18} />} color="#3B82F6" />
        <StatCard label="By System" value={bySource.system} icon={<XCircle size={18} />} color="#8B5CF6" />
      </div>

      <div className="admin-card" style={{ marginBottom: 24 }}>
        <div className="admin-card-header"><h3 className="admin-card-title">Rejections by Reason</h3></div>
        <div className="admin-card-body">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="reason" type="category" width={140} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#EF4444" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <span style={{ fontSize: 14, fontWeight: 700 }}>{data.length} Rejections</span>
        </div>
        <Table columns={columns} data={data} loading={loading} />
      </div>

      <Modal open={!!view} onClose={() => setView(null)} title="Rejection Details"
        footer={<Button variant="secondary" onClick={() => setView(null)}>Close</Button>}>
        {view && (
          <div className="admin-info-grid">
            {[
              ['Order ID', `#${view.orderId}`], ['Customer', view.customerName],
              ['Restaurant', view.restaurantName], ['Amount', formatCurrency(view.amount)],
              ['Rejection Reason', view.reason], ['Rejected By', view.rejectedBy],
              ['Date & Time', formatDateTime(view.rejectedAt)],
              ...(view.notes ? [['Notes', view.notes]] : []),
            ].map(([l, v]) => (
              <div key={l} className="admin-info-item">
                <span className="admin-info-label">{l}</span>
                <span className="admin-info-value">{v}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrderRejections;
